/**
 * Mensalidades do cliente (spec 007, secao 12; spec 041; spec 047): por plano, a situacao (em dia, vence em breve,
 * vencida) e cada mensalidade, com os dois jeitos de pagar: o "Pagar" (Pagar.me, PIX ou cartao, baixa na hora) e o
 * "Pagar pelo PIX direto", que pede o comprovante na mesma tela. Um administrador confere o comprovante e da a baixa;
 * ate la, a vencida segue bloqueando a area. A avulsa lancada por um administrador aparece como "Avulsa N", ou com o
 * nome dela quando tem (spec 071: "Multa de reativação"), na data que ele deu, e embaixo vem o calendario previsto das
 * proximas (so leitura). Situacao, valores e textos vem do backend.
 * A taxa de manutencao da Funded (spec 064) usa a mesma tela: a API manda o nome ("Taxa de manutenção"), o valor em
 * dolar com o real pela cotacao, e a parcela que suspendeu o plano com o ultimo dia pra pagar.
 */
import { Aviso, CabecalhoDePagina, Carregando, Fatos, formatarDia, formatarDolares, formatarReais, ListaDeParcelas, mensagemDe, Paginacao, paginar, Pilha, Recolhivel, Secao, Selo, Vazio, type Tom } from "@mtc/ui";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "../acoes";
import { useDentroDeAssunto } from "../assunto";
import { useFiltroDePlano } from "../planoAtual";
import { PagarCobranca } from "./comprovantes";
import { useMatriculas } from "./Planos";
import { cotacaoLegivel, nomeDaCobranca, tituloDaCobranca, valorDaParcela } from "../cobranca";
import { textoDaSuspensao } from "../passo";
import { tituloDaParcela, type ListaDeMensalidades, type Mensalidade, type MensalidadesDoPlano, type SituacaoMensalidade } from "../tipos";
import { usePaginaLocal } from "../pagina";

const TOM: Record<SituacaoMensalidade | "PAGA" | "CANCELADA", Tom> = { EM_DIA: "info", VENCE_EM_BREVE: "alerta", VENCE_HOJE: "alerta", VENCIDA: "erro", VALOR_A_DEFINIR: "neutro", SEM_COBRANCA: "neutro", SUSPENSA: "erro", PAGA: "sucesso", CANCELADA: "neutro" };

export function useMensalidades() {
  const { ler } = useApi();
  return useQuery({ queryKey: ["mensalidades"], queryFn: () => ler<ListaDeMensalidades>("/v1/me/mensalidades") });
}

export function PaginaMensalidades() {
  const q = useMensalidades();
  const filtro = useFiltroDePlano();
  const dentroDeAssunto = useDentroDeAssunto();
  if (q.isLoading) return <Carregando compacto texto="Buscando suas mensalidades" />;
  if (q.isError) return <Aviso tipo="erro">{mensagemDe(q.error, "Não conseguimos carregar suas mensalidades agora. Recarregue a página.")}</Aviso>;
  const itens = (q.data?.itens ?? []).filter((i) => !filtro.matriculaId || i.matricula.id === filtro.matriculaId);
  const rotulo = q.data?.rotulo;
  const descricao = rotulo
    ? `A ${rotulo.toLowerCase()} de cada plano na Funded. Pague por PIX ou cartão, com confirmação na hora, ou pelo PIX direto, enviando o comprovante aqui mesmo: um administrador confere em até 1 dia.`
    : undefined;
  return (
    <Pilha espaco={6}>
      {!dentroDeAssunto && <CabecalhoDePagina titulo={tituloDaCobranca(rotulo)} descricao={descricao ?? "A mensalidade de cada plano, a cada 30 dias. Pague por PIX ou cartão, com confirmação na hora, ou pelo PIX direto, enviando o comprovante aqui mesmo: um administrador confere em até 1 dia. Vencida, a área fica bloqueada até o pagamento ser confirmado."} />}
      {!itens.length ? <Vazio titulo="Nenhum plano ainda">Quando você comprar um plano, a {nomeDaCobranca(rotulo).toLowerCase()} dele aparece aqui.</Vazio> : itens.map((i) => <DoPlano key={i.matricula.id} i={i} />)}
    </Pilha>
  );
}

function DoPlano({ i }: { i: MensalidadesDoPlano }) {
  const pagPagas = usePaginaLocal();
  const r = i.resumo;
  // O bloqueio vem do backend (spec 047, 2.3): com o comprovante em analise a area segue bloqueada, so muda a frase.
  const bloqueio = useMatriculas().data?.itens.find((m) => m.id === i.matricula.id)?.bloqueio;
  const abertas = i.mensalidades.filter((m) => m.status === "A_PAGAR");
  const pagas = i.mensalidades.filter((m) => m.status === "PAGA");
  const previstas = i.previstas ?? [];
  // A ultima paga pode ser uma avulsa: o titulo sai da parcela, que sabe.
  const ultima = r?.ultima_paga ? pagas.find((m) => m.numero === r.ultima_paga!.numero && m.vencimento === r.ultima_paga!.vencimento) : undefined;
  // Na Prop a resposta nao tem nome (e a "Mensalidade"); a cobranca com nome proprio e a taxa da Funded (spec 064).
  const nome = nomeDaCobranca(i.rotulo ?? r?.rotulo);
  const comNome = Boolean(i.rotulo ?? r?.rotulo);
  // Suspenso pela taxa: a parcela que suspendeu traz o ultimo dia pra pagar e se o prazo ja passou.
  const suspensora = abertas.find((m) => m.suspensa_ate) ?? null;
  const suspenso = r?.situacao === "SUSPENSA" && (r.suspensa_ate ?? suspensora?.suspensa_ate);
  // O dolar ao lado do real tem que ser o da mesma parcela (spec 065): a que ja nasceu guarda o valor dela, mesmo que o
  // administrador tenha mudado o do plano depois. Sem parcela aberta, o valor de agora do plano.
  const usd = r?.proxima?.valor_usd_centavos ?? abertas[0]?.valor_usd_centavos ?? r?.valor_usd_centavos ?? null;
  const emReais = r?.proxima?.valor_centavos ?? abertas[0]?.valor_centavos ?? null;
  const cotacao = r?.proxima?.cotacao ?? abertas[0]?.cotacao ?? null;
  const titulo = suspenso ? `${nome} em atraso` : r?.paga_mensalidade || abertas.length ? `${nome} ${r?.situacao_rotulo.toLowerCase() ?? ""}`.trim() : `Sem ${nome.toLowerCase()} agora`;
  return (
    <Secao destaque={r?.situacao === "VENCIDA" || Boolean(suspenso)} rotulo={i.matricula.plano.nome} titulo={titulo} acoes={r ? <Selo tom={TOM[r.situacao]}>{r.situacao_rotulo}</Selo> : undefined}>
      {suspenso && <Aviso tipo="erro">{textoDaSuspensao({ ate: suspenso, encerrado: Boolean(suspensora?.prazo_encerrado) })}</Aviso>}
      {r?.situacao === "VENCIDA" && comNome && <Aviso tipo="erro">A {nome.toLowerCase()} venceu e o plano é suspenso se ela não for paga. Pague abaixo: por PIX ou cartão, com confirmação na hora, ou pelo PIX direto, com o comprovante.</Aviso>}
      {r?.situacao === "VENCIDA" && !comNome && (bloqueio?.motivo === "COMPROVANTE_EM_ANALISE"
        ? <Aviso tipo="alerta">A mensalidade venceu e a área segue bloqueada até um administrador conferir o seu comprovante.</Aviso>
        : <Aviso tipo="erro">A mensalidade venceu e a área está bloqueada. Pague abaixo: por PIX ou cartão a área volta na hora; pelo PIX direto, com o comprovante, quando um administrador conferir, em até 1 dia.</Aviso>)}
      {!r?.paga_mensalidade && !abertas.length && <p className="mtc-texto-2" style={{ margin: 0 }}>{r?.sem_cobranca_motivo ?? "Este plano não tem mensalidade em aberto."}</p>}
      {(r?.paga_mensalidade || abertas.length > 0) && (
        <Fatos
          itens={[
            usd != null
              ? { rotulo: "Valor", valor: formatarDolares(usd), mono: true, detalhe: emReais != null && cotacao ? `${formatarReais(emReais)} pela cotação de ${cotacaoLegivel(cotacao)}` : "Em reais pela cotação do dia da cobrança" }
              : { rotulo: "Valor", valor: r?.valor_centavos != null ? formatarReais(r.valor_centavos) : abertas[0]?.valor_centavos != null ? formatarReais(abertas[0].valor_centavos) : "A definir", mono: true },
            // A taxa da Funded nasce alguns dias antes do vencimento (spec 065, configuravel): antes disso, a proxima e a primeira prevista.
            { rotulo: "Próxima", valor: r?.proxima ? r.proxima.vencimento_formatado : comNome && previstas[0] ? previstas[0].vencimento_formatado : "Em dia" },
            ...(r?.ultima_paga ? [{ rotulo: "Última paga", valor: formatarDia(r.ultima_paga.vencimento), detalhe: tituloDaParcela({ numero: r.ultima_paga.numero, avulsa: ultima?.avulsa, rotulo: ultima?.rotulo }) }] : []),
          ]}
        />
      )}
      {/* A regra do plano pronta do backend (spec 065): o valor deste plano, o ciclo e quando a parcela nasce. */}
      {comNome && r?.paga_mensalidade && r.texto && <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>{r.texto}</p>}
      {abertas.map((m) => <EmAberto key={m.id} m={m} />)}
      {pagas.length > 0 && (
        <Pilha espaco={2}>
          <span className="mtc-rotulo">Pagas</span>
          <ListaDeParcelas
            rotulo={`${comNome ? `${nome}: parcelas pagas` : "Mensalidades pagas"} do ${i.matricula.plano.nome}`}
            itens={paginar(pagas, pagPagas.pagina).map((m) => ({
              chave: m.id,
              titulo: `${tituloDaParcela(m)}${m.do_avanco ? " (no avanço)" : ""}`,
              vencimento: m.vencimento_formatado,
              valor: m.valor_centavos != null || m.valor_usd_centavos != null ? valorDaParcela(m) : undefined,
              selo: { tom: "sucesso", texto: m.como_pagou === "POR_FORA" ? "Paga (confirmada)" : "Paga" },
            }))}
          />
          <Paginacao pagina={pagPagas.pagina} total={pagas.length} aoMudar={pagPagas.aoMudar} rotulo={`Páginas das ${comNome ? "parcelas" : "mensalidades"} pagas do ${i.matricula.plano.nome}`} />
        </Pilha>
      )}
      {/* O calendario das proximas (041, 3.7): so leitura, com o valor de agora; nao se paga nem bloqueia. Fechado: e consulta. */}
      {previstas.length > 0 && (
        <Recolhivel titulo="Próximas (previstas)" apoio={`${previstas.length} ${comNome ? (previstas.length === 1 ? "parcela" : "parcelas") : previstas.length === 1 ? "mensalidade" : "mensalidades"} até ${previstas[previstas.length - 1]!.vencimento_formatado}`}>
          <Pilha espaco={3}>
            <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>{usd != null
              ? "O calendário das próximas parcelas, com o valor em dólar: o valor em reais sai na cotação do dia da cobrança. Cada uma vira cobrança perto do vencimento e aí aparece acima, com o botão de pagar."
              : "O calendário das próximas mensalidades, com o valor de hoje. Cada uma vira cobrança perto do vencimento e aí aparece acima, com o botão de pagar."}</p>
            <ListaDeParcelas
              grade
              rotulo={`${comNome ? `${nome}: parcelas previstas` : "Mensalidades previstas"} do ${i.matricula.plano.nome}`}
              itens={previstas.map((m) => ({
                chave: `prevista-${m.numero}`,
                // A prevista nao traz titulo: com o nome da API vira "Taxa de manutenção 2".
                titulo: comNome ? `${nome} ${m.numero}` : tituloDaParcela(m),
                vencimento: m.vencimento_formatado,
                valor: valorDaParcela(m),
                selo: { tom: "neutro", texto: "Prevista" },
                prevista: true,
              }))}
            />
          </Pilha>
        </Recolhivel>
      )}
    </Secao>
  );
}

function EmAberto({ m }: { m: Mensalidade }) {
  return (
    <div className="mtc-pilha" style={{ gap: "var(--e-3)", padding: "var(--e-4)", border: "1px solid var(--borda)", borderRadius: "var(--r-2)" }} data-mensalidade={m.avulsa ? `avulsa-${m.numero}` : m.numero}>
      <div className="mtc-linha mtc-linha--entre" style={{ gap: "var(--e-2)" }}>
        <strong>{tituloDaParcela(m)}</strong>
        <Selo tom={TOM[m.situacao]}>{m.situacao_rotulo}</Selo>
      </div>
      <p style={{ margin: 0 }}>
        {m.valor_usd_centavos != null ? (
          // A taxa em dolar (spec 064): o dolar e o real com a cotacao que a API mandou.
          <><strong className="mtc-mono">{formatarDolares(m.valor_usd_centavos)}</strong>{m.valor_centavos != null && m.cotacao ? <>, hoje <strong className="mtc-mono">{formatarReais(m.valor_centavos)}</strong> pela cotação de {cotacaoLegivel(m.cotacao)}</> : ", o valor em reais sai com a cotação do dólar"}</>
        ) : m.valor_centavos != null ? <strong className="mtc-mono">{formatarReais(m.valor_centavos)}</strong> : "Valor a definir"}, vence em {m.vencimento_formatado}
        {m.dias_de_atraso > 0 ? ` (${m.dias_de_atraso} ${m.dias_de_atraso === 1 ? "dia" : "dias"} de atraso)` : ""}.
      </p>
      {!m.pode_pagar && m.valor_usd_centavos != null && <Aviso tipo="info">O valor em reais sai com a cotação do dólar, que está a caminho. O botão de pagar aparece aqui assim que ela chegar.</Aviso>}
      {!m.pode_pagar && m.valor_usd_centavos == null && <Aviso tipo="info">O valor desta mensalidade ainda não foi definido. Um administrador já foi avisado; o botão de pagar aparece aqui quando o valor estiver definido.</Aviso>}
      <PagarCobranca tipo="MENSALIDADE" id={m.id} valorCentavos={m.valor_centavos} podePagar={m.pode_pagar} />
    </div>
  );
}
